import type { Camera, Celula, Regiao } from '../tipos';
import { regioesSeCruzam } from './coordenadas';

/**
 * Câmera do tabuleiro (§7.2, T07).
 *
 * Arrastar e dar zoom mexem **só** na câmera; coordenadas de palavras nunca
 * mudam (T07-01). Isso mantém a cadeia estável e o desfazer determinístico.
 */

export const ZOOM_MIN = 0.5;
export const ZOOM_MAX = 2;
export const ZOOM_PASSO = 0.1;

/** Lado da célula em pixels a 100%. */
export const LADO_CELULA = 48;

export const CAMERA_INICIAL: Camera = { x: 0, y: 0, zoom: 1 };

export function limitarZoom(zoom: number): number {
  return Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, zoom));
}

export function mover(camera: Camera, dx: number, dy: number): Camera {
  return { ...camera, x: camera.x + dx, y: camera.y + dy };
}

/**
 * Zoom em torno de um ponto da tela (T07-02): o ponto sob o cursor ou sob os
 * dedos permanece sob eles.
 */
export function aplicarZoom(camera: Camera, fator: number, pontoX: number, pontoY: number): Camera {
  const zoom = limitarZoom(camera.zoom * fator);
  if (zoom === camera.zoom) return camera;
  const escala = zoom / camera.zoom;
  return {
    zoom,
    x: pontoX - (pontoX - camera.x) * escala,
    y: pontoY - (pontoY - camera.y) * escala,
  };
}

/** Converte célula da grade para pixels do mundo (antes da câmera). */
export function celulaParaMundo(c: Celula): { x: number; y: number } {
  return { x: c.coluna * LADO_CELULA, y: c.linha * LADO_CELULA };
}

/** Converte pixels de tela para célula da grade. */
export function telaParaCelula(camera: Camera, telaX: number, telaY: number): Celula {
  const mundoX = (telaX - camera.x) / camera.zoom;
  const mundoY = (telaY - camera.y) / camera.zoom;
  return {
    coluna: Math.floor(mundoX / LADO_CELULA),
    linha: Math.floor(mundoY / LADO_CELULA),
  };
}

/** Região da grade visível na viewport atual. Base da virtualização (§16). */
export function regiaoVisivel(camera: Camera, largura: number, altura: number): Regiao {
  const inicio = telaParaCelula(camera, 0, 0);
  const fim = telaParaCelula(camera, largura, altura);
  return {
    colunaMin: inicio.coluna,
    linhaMin: inicio.linha,
    colunaMax: fim.coluna,
    linhaMax: fim.linha,
  };
}

/**
 * Centraliza a região ativa preservando o nível de zoom atual (T07-05).
 */
export function centralizarEm(
  camera: Camera,
  regiao: Regiao,
  largura: number,
  altura: number,
): Camera {
  const centroColuna = (regiao.colunaMin + regiao.colunaMax + 1) / 2;
  const centroLinha = (regiao.linhaMin + regiao.linhaMax + 1) / 2;
  return {
    zoom: camera.zoom,
    x: largura / 2 - centroColuna * LADO_CELULA * camera.zoom,
    y: altura / 2 - centroLinha * LADO_CELULA * camera.zoom,
  };
}

/**
 * O botão `Voltar à palavra atual` aparece quando a área ativa está fora ou
 * quase toda fora da viewport (T07-04).
 */
export function precisaRecentralizar(areaAtiva: Regiao, visivel: Regiao): boolean {
  if (!regioesSeCruzam(areaAtiva, visivel)) return true;

  const larguraAtiva = areaAtiva.colunaMax - areaAtiva.colunaMin + 1;
  const alturaAtiva = areaAtiva.linhaMax - areaAtiva.linhaMin + 1;
  const larguraVisivel =
    Math.min(areaAtiva.colunaMax, visivel.colunaMax) -
    Math.max(areaAtiva.colunaMin, visivel.colunaMin) +
    1;
  const alturaVisivel =
    Math.min(areaAtiva.linhaMax, visivel.linhaMax) -
    Math.max(areaAtiva.linhaMin, visivel.linhaMin) +
    1;

  const visivelProporcao =
    (Math.max(0, larguraVisivel) * Math.max(0, alturaVisivel)) / (larguraAtiva * alturaAtiva);

  // "Quase toda fora": menos de um terço da área ativa aparece.
  return visivelProporcao < 1 / 3;
}
